import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Check, ArrowRight, Video, MessagesSquare, TrendingUp, CalendarDays } from "lucide-react";
import Seo from "../components/Seo";
import CaptionImage from "../components/CaptionImage";
import { Reveal, MaskedLines, Chapter } from "../components/Motion";
import { Field, SubmitButton, useSubmission } from "../components/Forms";
import { createCheckout } from "../lib/api";
import { IMAGES } from "../site/content";

const FLEX_POINTS = [
  "On-demand video lessons so learners study at the hour that works for them",
  "Live 1:1 tuition for questions, practice, and feedback",
  "Step-by-step modules that can be paused, replayed, and resumed",
  "School-year and exam-paced Maths tracks; beginner-to-enthusiast Cooking tracks; age-open Art tracks (drawing, painting, digital)",
];

const FLEX_MODEL = [
  { icon: Video, step: "01", title: "Watch when you want", text: "Recorded lessons available on demand — study at the hour that works for you." },
  { icon: MessagesSquare, step: "02", title: "Practise with a tutor", text: "Live 1:1 tuition for questions, practice, and feedback that fits your level." },
  { icon: TrendingUp, step: "03", title: "Progress at your pace", text: "Pause, replay, and resume modules freely. Progress is measured in skill." },
];

const SUBJECTS = [
  {
    id: "mathematics",
    number: "01",
    title: "Mathematics",
    text: "Concept-first lessons and personal support for school and competitive exams. Tracks follow the school year or your exam calendar — algebra to advanced topics, each idea built before the next.",
    points: ["School-year tracks aligned to class", "Exam-paced tracks for competitive tests", "Concept-first teaching, then drilling", "Doubt-clearing in live 1:1 sessions"],
    img: IMAGES.mathClass,
  },
  {
    id: "cooking",
    number: "02",
    title: "Cooking",
    text: "Practical video guidance for beginners and enthusiasts. Learn technique, timing, and taste — from foundational knife work and everyday meals to dishes worth inviting people for.",
    points: ["Beginner-to-enthusiast tracks", "Step-by-step filmed technique", "Ingredients you can actually find", "Live cook-along feedback sessions"],
    img: IMAGES.cookingClass,
  },
  {
    id: "art",
    number: "03",
    title: "Art",
    text: "Structured tutorials plus personalised tuition, open to every age. Drawing, painting, and digital art — with a tutor who reviews your actual work, not just the lesson.",
    points: ["Age-open tracks for every learner", "Drawing, painting, and digital", "Structured tutorials plus 1:1 critique", "Portfolio-friendly projects"],
    img: IMAGES.artClass,
  },
];

const WEEK = [
  { step: "Day 1–2", title: "Two recorded lessons", text: "Watch when it suits you. Pause, rewind, and rewatch the hard parts." },
  { step: "Day 3–4", title: "Practice homework", text: "Short, targeted tasks that lock each lesson in — not busywork." },
  { step: "Day 5", title: "One live 1:1 session", text: "Bring your questions and your work. Leave with feedback and a plan." },
  { step: "Always", title: "Resume where you left off", text: "Modules remember your place. Busy week? The course waits for you." },
];

const TUITION_PLANS = [
  {
    id: "trial-diagnostic",
    title: "1:1 Diagnostic Trial",
    price: 25,
    subtitle: "One live 45-minute lesson with an expert tutor",
    features: [
      "Diagnostic skill assessment",
      "Choice of Maths, Cooking, or Art",
      "Tailored study roadmap",
      "Credit applied if you enrol monthly",
    ],
  },
  {
    id: "monthly-pass",
    title: "Monthly Tuition Pass",
    price: 120,
    subtitle: "Complete flexibility — 8 lessons + 4 live sessions",
    features: [
      "2 recorded video lessons per week",
      "1 live 1:1 tutoring session per week",
      "Personalised practice homework review",
      "Pause, replay, and resume anytime",
    ],
    popular: true,
  },
  {
    id: "term-track",
    title: "Full Term Track (12 Wks)",
    price: 320,
    subtitle: "Comprehensive exam or skill mastery",
    features: [
      "24 structured video modules",
      "12 live 1:1 coaching hours",
      "Exam-specific drill questions & feedback",
      "Certificate of skill completion",
    ],
  },
];

function TuitionCheckout() {
  const [loadingId, setLoadingId] = useState(null);

  const startCheckout = async (plan) => {
    setLoadingId(plan.id);
    try {
      const { checkout_url } = await createCheckout({
        kind: "tuition",
        amount: plan.price,
        method: "Credit Card",
        note: `Tuition: ${plan.title}`,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoadingId(null);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-3" data-testid="tuition-plans-grid">
      {TUITION_PLANS.map((plan) => (
        <div
          key={plan.id}
          className={`flex flex-col justify-between rounded-3xl border p-6 sm:p-8 shadow-sm transition-transform duration-300 hover:-translate-y-1 ${
            plan.popular
              ? "border-teal bg-white shadow-md ring-2 ring-teal/30"
              : "border-line bg-white/80"
          }`}
          data-testid={`tuition-plan-${plan.id}`}
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal">
                {plan.popular ? "★ Most Popular" : "Tuition Plan"}
              </span>
            </div>
            <h3 className="mt-2 font-serif text-2xl font-bold text-charcoal">{plan.title}</h3>
            <p className="mt-1 text-xs text-charcoal-muted">{plan.subtitle}</p>
            <div className="my-6 border-y border-line py-4">
              <span className="font-serif text-4xl font-bold text-teal">${plan.price}</span>
              <span className="ml-2 text-xs font-mono text-charcoal/60">USD</span>
            </div>
            <ul className="space-y-2.5">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-charcoal/80">
                  <Check size={16} className="shrink-0 text-teal mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-8">
            <button
              onClick={() => startCheckout(plan)}
              disabled={loadingId === plan.id}
              className={`w-full text-sm ${plan.popular ? "btn-primary" : "btn-outline"}`}
              data-testid={`tuition-checkout-btn-${plan.id}`}
            >
              <CreditCard size={16} />
              {loadingId === plan.id ? "Opening Stripe…" : `Enrol for $${plan.price}`}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function TrialForm() {
  const { sending, sent, send } = useSubmission("trial_lesson");
  const [form, setForm] = useState({ name: "", email: "", subject: "Mathematics", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (sent) {
    return (
      <div className="rounded-3xl border border-teal/30 bg-teal/5 p-8 text-center" data-testid="trial-success">
        <p className="font-serif text-2xl text-teal">Trial lesson requested.</p>
        <p className="mt-3 text-sm text-charcoal-muted">We will write back with times and a tutor match.</p>
      </div>
    );
  }

  return (
    <form
      className="rounded-3xl border border-line bg-white/70 p-6 sm:p-9"
      data-testid="trial-form"
      onSubmit={(e) => {
        e.preventDefault();
        send(form);
      }}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Learner name" htmlFor="trial-name">
          <input id="trial-name" required className="field" value={form.name} onChange={set("name")} data-testid="trial-name-input" placeholder="First name is fine" />
        </Field>
        <Field label="Email" htmlFor="trial-email">
          <input id="trial-email" required type="email" className="field" value={form.email} onChange={set("email")} data-testid="trial-email-input" placeholder="you@example.com" />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Subject" htmlFor="trial-subject">
          <select id="trial-subject" className="field" value={form.subject} onChange={set("subject")} data-testid="trial-subject-select">
            <option>Mathematics</option>
            <option>Cooking</option>
            <option>Art</option>
          </select>
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Anything we should know" htmlFor="trial-message">
          <textarea id="trial-message" rows={4} className="field" value={form.message} onChange={set("message")} data-testid="trial-message-input" placeholder="Level, goals, exam dates, preferred times…" />
        </Field>
      </div>
      <div className="mt-7">
        <SubmitButton sending={sending} testid="trial-submit-button">Book a trial lesson</SubmitButton>
      </div>
    </form>
  );
}

export default function Learn() {
  return (
    <>
      <Seo
        title="Learn — Mathematics, Cooking & Art Tuition"
        description="Flexible video lessons plus live 1:1 tuition in Mathematics, Cooking, and Art. Watch when you want, practise with a tutor, progress at your pace."
        path="/learn"
      />

      <section className="pt-28 sm:pt-36 pb-14 sm:pb-20" data-testid="learn-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="eyebrow flex items-center gap-3">
                <span className="h-px w-8 bg-teal" aria-hidden="true" />
                Learn with ProOne
              </p>
              <MaskedLines
                lines={["Real skills,", <span key="t" className="italic text-teal">on your schedule.</span>]}
                className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-charcoal"
              />
              <Reveal delay={0.5}>
                <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-charcoal-muted">
                  <strong className="text-teal">Beyond business solutions, ProOne is passionate about learning and education.</strong>{" "}
                  Flexibility here is not a slogan — it is the structure of every course:
                </p>
                <ul className="mt-7 space-y-4">
                  {FLEX_POINTS.map((p) => (
                    <li key={p} className="flex gap-3 text-sm sm:text-base leading-relaxed text-charcoal/90">
                      <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rotate-45 bg-teal" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.3}>
                <CaptionImage image={IMAGES.learnTutor} ratio="aspect-[4/5]" rounded="rounded-[2rem]" priority />
              </Reveal>
            </div>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-3" data-testid="learn-flexibility-model">
            {FLEX_MODEL.map((f, i) => (
              <Reveal key={f.step} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-line bg-white/60 p-6">
                  <f.icon size={24} className="text-teal" strokeWidth={1.7} aria-hidden="true" />
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">{f.step}</p>
                  <p className="mt-1.5 font-serif text-xl font-semibold text-charcoal">{f.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {SUBJECTS.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`py-16 sm:py-24 ${i % 2 === 1 ? "bg-sand/60 border-y border-line" : ""}`}
          data-testid={`subject-section-${s.id}`}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Chapter number={s.number} eyebrow="Subject" title={s.title} />
            <div className="grid items-start gap-10 lg:grid-cols-12">
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <Reveal>
                  <p className="text-base sm:text-lg leading-relaxed text-charcoal">{s.text}</p>
                  <ul className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex gap-3 text-sm leading-relaxed text-charcoal/90">
                        <span className="mt-[8px] h-1.5 w-1.5 shrink-0 rotate-45 bg-teal" aria-hidden="true" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <Reveal delay={0.1}>
                  <CaptionImage image={s.img} ratio="aspect-[4/3]" rounded="rounded-[2rem]" />
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="py-16 sm:py-24 bg-sand/60 border-y border-line" data-testid="week-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="04" eyebrow="The rhythm" title="A week with ProOne might look like this" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="week-grid">
            {WEEK.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-line bg-cream p-6">
                  <CalendarDays size={20} className="text-teal" strokeWidth={1.7} aria-hidden="true" />
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">{w.step}</p>
                  <p className="mt-1.5 font-serif text-xl font-semibold text-charcoal">{w.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm text-charcoal-muted">
              Two recorded lessons, one live session, and practice homework — shaped around your week, not ours.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white/70 border-t border-line" data-testid="tuition-checkout-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="05" eyebrow="Enrol Online" title="Choose a tuition plan and start with Stripe." />
          <Reveal>
            <p className="mb-10 max-w-2xl text-sm sm:text-base leading-relaxed text-charcoal-muted">
              Secure immediate access through Stripe checkout, or request a customized trial lesson below.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <TuitionCheckout />
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-teal-dark" data-testid="trial-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <Chapter number="06" eyebrow="Start learning" title="Book a free consultation / trial." dark />
              <Reveal delay={0.1}>
                <p className="text-sm sm:text-base leading-relaxed text-cream/75">
                  One lesson, one tutor, no commitment. If it fits, we build your track.
                  If it doesn't, you keep the lesson.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <TrialForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
